import { companyStatusChangeLabel, dayKey, fmtDayTime, plural } from '@remote-medium/shared';
import {
  ArrowRightLeft,
  Briefcase,
  Building2,
  Check,
  CircleCheck,
  Handshake,
  ListChecks,
  Mail,
  NotebookPen,
  Ticket,
  type LucideIcon,
} from 'lucide-react';

import { H2 } from '../../../lib/ui.tsx';
import type { Company, CompanyContext } from './context.ts';

interface Event {
  at: string;
  icon: LucideIcon;
  title: string;
}

/** Groups timestamps by calendar day; each group keeps its latest time. */
function byDay(stamps: string[]) {
  const groups = new Map<string, { at: string; n: number }>();
  for (const at of stamps) {
    const g = groups.get(dayKey(at));
    if (!g) groups.set(dayKey(at), { at, n: 1 });
    else groups.set(dayKey(at), { at: at > g.at ? at : g.at, n: g.n + 1 });
  }
  return [...groups.values()];
}

// Built from the timestamps the API returns, the status log (suspensions and
// lifts), each role's ranking runs (the first, and any that added matches),
// the curator notes (a deleted note leaves no trace) and the company's email
// log (emails about its roles).
function events(c: Company, ctx: CompanyContext | null): Event[] {
  const out: Event[] = [{ at: c.createdAt, icon: Building2, title: 'Company signed up' }];
  const fee = c.accountFeePaidAt ?? c.feeWaivedAt ?? c.feeExemptAt;
  const feeTitle = c.accountFeePaidAt
    ? 'Account fee paid'
    : c.feeWaivedAt
      ? `Account fee waived by ${c.feeWaivedByName ?? 'a curator'}`
      : 'Account fee not owed (launch period)';
  // Whichever gate closed last is the moment the account went live. An
  // exemption set at activation shares its timestamp, so it reads as last.
  const feeLast = !!fee && !!c.activatedAt && fee >= c.activatedAt;
  if (c.activatedAt)
    out.push({
      at: c.activatedAt,
      icon: Check,
      title: `Sanity check passed by ${c.activatedByName ?? 'a curator'}${fee && !feeLast ? ', account activated' : ''}`,
    });
  if (fee)
    out.push({
      at: fee,
      icon: CircleCheck,
      title: feeLast ? `${feeTitle}, account activated` : feeTitle,
    });
  for (const change of c.statusChanges) {
    const title = companyStatusChangeLabel(change);
    if (title) out.push({ at: change.at, icon: ArrowRightLeft, title });
  }
  for (const n of c.notes)
    out.push({ at: n.at, icon: NotebookPen, title: `Note added by ${n.byName}` });
  for (const b of c.bundles)
    out.push({
      at: b.purchasedAt,
      icon: Ticket,
      title: `${plural(b.totalCredits, 'role credit')} added`,
    });

  const posters = new Map(c.users.map((u) => [u.id, u.fullName ?? u.email]));
  for (const { role, matches, ranks } of ctx?.roles ?? []) {
    const by = role.postedByCompanyUserId
      ? posters.get(role.postedByCompanyUserId)
      : role.postedByCuratorId
        ? `${role.postedByCuratorName ?? 'a curator'}, on their behalf`
        : undefined;
    out.push({
      at: role.createdAt,
      icon: Briefcase,
      title: `${role.title} posted${by ? ` by ${by}` : ''}`,
    });
    for (const r of ranks)
      out.push({
        at: r.at,
        icon: ListChecks,
        title: r.first
          ? `${role.title} ranked, ${plural(r.matched, 'candidate')} above threshold`
          : `${role.title} re-ranked, ${plural(r.newMatches, 'new candidate')} above threshold`,
      });
    for (const g of byDay(matches?.introSentAt ?? []))
      out.push({
        at: g.at,
        icon: Handshake,
        title: `${plural(g.n, 'introduction')} sent for ${role.title}`,
      });
    const consent = (ctx?.emailLog ?? []).filter(
      (e) => e.template === 'consent_request' && e.relatedRoleId === role.id,
    );
    for (const g of byDay(consent.map((e) => e.sentAt)))
      out.push({
        at: g.at,
        icon: Mail,
        title: `${plural(g.n, 'consent request')} sent for ${role.title}`,
      });
  }
  return out.sort((a, b) => b.at.localeCompare(a.at));
}

export function Activity({ c, ctx }: { c: Company; ctx: CompanyContext | null }) {
  return (
    <section className="flex flex-col gap-3 px-1">
      <h2 className={H2}>Activity</h2>
      <ol className="m-0 flex list-none flex-col gap-4 p-0">
        {events(c, ctx).map((e, i) => (
          <li key={i} className="flex items-start gap-3">
            <e.icon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div className="flex min-w-0 flex-col">
              <span className="text-sm">{e.title}</span>
              <span className="text-xs text-muted-foreground">{fmtDayTime(e.at)}</span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
